
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Users, Award, Target, Music, Globe, Headphones, ArrowRight, Star, CheckCircle2, Check } from "lucide-react";
import useScrollToTop from "@/hooks/useScrollToTop";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Helmet } from "react-helmet-async";
import { DASHBOARD_SIGNIN_URL } from "@/config/urls";

const About = () => {
  useScrollToTop();

  const milestones = [
    { icon: <Users size={48} className="text-ophira-yellow" />, number: "50K+", label: "Artists Worldwide" },
    { icon: <Globe size={48} className="text-ophira-yellow" />, number: "150+", label: "Streaming Platforms" },
    { icon: <Music size={48} className="text-ophira-yellow" />, number: "100%", label: "Royalties Kept" }
  ];

  const leaders = [
    {
      name: "Saurabh Raj",
      title: "Director",
      description: "Engineering from IIIT-Allahabad, MBA from ICFAI Business Schools. 18 years of Work Experience in Top Corporates.",
      image: "/uploads/saurabh-director.webp"
    },
    {
      name: "Mayank Singh",
      title: "Founder & CEO",
      description: "Business Graduate and Music Business Professional with more than 7 years of Experience.",
      image: "/uploads/mayank-ceo.webp"
    },
    {
      name: "Aryan Saini",
      title: "Co-Founder & CTO",
      description: "Expertise in building and scaling secure platforms and is the brain behind all our technology and products.",
      image: "/uploads/aryan-cto.webp"
    },
    {
      name: "Mohit Bansal",
      title: "Co-Founder & COO",
      description: "Mohit is the backbone of YouAux, taking care of operations and customer support.",
      image: "/uploads/mohit-coo.webp"
    }
  ];

  const trustpilotReviews = [
    {
      name: "Shay Brew",
      country: "US",
      reviewCount: "1 review",
      date: "September 13, 2025",
      rating: 5,
      quote: "YouAux Has Tremendously Came In Clutch For me Especially on the Releasing Music Tip And I am Overly Grateful I just Hope you don't get Sooo popular that you start changing stuff around and become money blind like United Masters Did Overall YouAux is One of the best Music App you can have",
      avatar: "https://user-images.trustpilot.com/69162df5aa3048e5796eacc7/73x73.png",
      verified: true
    },
    {
      name: "Ginger Ntuli",
      country: "ZA",
      reviewCount: "2 reviews",
      date: "September 26, 2025",
      rating: 5,
      quote: "Super fast and reliable, they distributed my music within 24 to 48hrs and had my issue fixed very fast...Love the service!!! by far one of the most fastest distro ever",
      verified: false
    },
    {
      name: "Casanya Ursery",
      country: "US",
      reviewCount: "1 review",
      date: "December 22, 2025",
      rating: 5,
      quote: "A great experience - especially the prompt and personal response of the customer service team!",
      avatar: "https://user-images.trustpilot.com/694ad08031cbb9ada67aee8c/73x73.png",
      verified: false
    },
    {
      name: "Peter Shauri",
      country: "KE",
      reviewCount: "2 reviews",
      date: "September 27, 2025",
      rating: 5,
      quote: "I really appreciate this website you are good in handling customers Your help is very important to us and I appreciate for that YouAux forever",
      verified: true
    },
    {
      name: "Segun Ayomide",
      country: "NG",
      reviewCount: "1 review",
      date: "November 24, 2025",
      rating: 4,
      quote: "I want to thank Youaux.com for their incredible support for artists. My experience with their customer care has been excellent; their advisors genuinely listen to what artists are going through. Unlike other platforms that often take critical, career-ruining actions (like permanent bans) without hearing the artist's side of the story, Youaux.com is professional, patient, and fair. They really mean business and treat artists with respect. I would give them a full 5 stars, but I believe there is room for improvement regarding payout times. The current 3-month waiting period is too long for independent artists, and I strongly suggest decreasing that timeframe. Overall, though, I really appreciate the platform",
      verified: true
    },
    {
      name: "Leafy",
      country: "RS",
      reviewCount: "1 review",
      date: "December 11, 2025",
      rating: 5,
      quote: "YouAux is really helpful music distribution platform, honestly i really like their support,they respond most of the time as soon as possible. If you make mistakes with your release they are more than glad to help you fix it!",
      avatar: "https://user-images.trustpilot.com/693ad0c600ecba1248834761/73x73.png",
      verified: false
    },
    {
      name: "Chandrashekhar Janawade",
      country: "IN",
      reviewCount: "1 review",
      date: "November 19, 2025",
      rating: 4,
      quote: "The Support, Reply, and Service is Great by Team YouAux. That technical issues always happens, but the team is very much supportive and step ahead for artists... wishing happy to join more with this Team. Jay Ho Jay Bharat🎶🎼🎶🎶🎼",
      verified: true
    },
    {
      name: "Coles Henderson",
      country: "US",
      reviewCount: "1 review",
      date: "October 4, 2025",
      rating: 5,
      quote: "This is a really good and easy to use distributor. I use their free plan, and getting my songs out is really easy. I will say, if you are distributing to Apple Music, you have to make sure you distribute your music a good few days before the release date, because Apple has to process releases on it's side, which can take a while and delay your music. However, while this issue is on Apple's side, YouAux helped when this happened to me by contacting Apple regarding the delay and resubmitting my release request to Apple on priority. Their customer service is great, and I highly recommend.",
      verified: false
    },
    {
      name: "Manoj Mane",
      country: "SG",
      reviewCount: "1 review",
      date: "October 29, 2025",
      rating: 5,
      quote: "The service is great! Thank you for supporting us, for the timely distribution of our music, and for responding so quickly",
      verified: true
    },
  ];

  const [reviewIndex, setReviewIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const [expandedReviews, setExpandedReviews] = useState<Record<string, boolean>>({});

  // Manual ordering/grouping to interleave pairs and singles cleanly
  const columnPlan: string[][] = [
    ["Peter Shauri"],
    ["Shay Brew"],
    ["Coles Henderson"],
    ["Leafy"],
    ["Ginger Ntuli"],
    ["Casanya Ursery"],
    ["Chandrashekhar Janawade"],
    ["Segun Ayomide"],
  ];

  const usedNames = new Set<string>();
  const reviewColumns: { items: typeof trustpilotReviews }[] = [];

  columnPlan.forEach((group) => {
    const items = group
      .map((name) => {
        const found = trustpilotReviews.find((r) => r.name === name);
        if (found) usedNames.add(name);
        return found;
      })
      .filter(Boolean) as typeof trustpilotReviews;
    if (items.length) {
      reviewColumns.push({ items });
    }
  });

  // Append any leftovers not explicitly placed
  trustpilotReviews.forEach((r) => {
    if (!usedNames.has(r.name)) {
      reviewColumns.push({ items: [r] as any });
    }
  });

  const sliderColumns = [...reviewColumns, ...reviewColumns.slice(0, 3)];

  const handleNext = () => {
    setIsTransitioning(true);
    setReviewIndex((prev) => (prev + 1) % reviewColumns.length);
  };

  const handlePrev = () => {
    setIsTransitioning(true);
    setReviewIndex((prev) => (prev - 1 + reviewColumns.length) % reviewColumns.length);
  };

  // Auto-advance
  useEffect(() => {
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setReviewIndex((prev) => prev + 1);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // Loop seamlessly when we hit the duplicated slides
  useEffect(() => {
    if (reviewIndex === reviewColumns.length) {
      const timeout = setTimeout(() => {
        setIsTransitioning(false);
        setReviewIndex(0);
      }, 350); // match transition duration
      return () => clearTimeout(timeout);
    } else {
      // Ensure transition is re-enabled after a jump
      const timeout = setTimeout(() => setIsTransitioning(true), 20);
      return () => clearTimeout(timeout);
    }
  }, [reviewIndex, reviewColumns.length]);

  return (
    <>
    <Helmet>
        <title>About | YouAux</title>
        <meta name="description" content="We're a passionate team revolutionizing music distribution through innovative technology and artist-friendly services." />
        <link rel="canonical" href="https://youaux.com/about" />
      </Helmet>
    <div className="ophira-bg min-h-screen bg-black">
      <Navbar />
      <main className="container mx-auto px-4 py-28">
        <section className="text-center mt-8 mb-16">
          <AboutHeader />
          {/* <p className="text-ophira-mediumgray/80 max-w-3xl mx-auto text-lg reveal-animation" style={{"--reveal-delay": "2"} as React.CSSProperties}>
            We're a passionate team revolutionizing music distribution through innovative technology and artist-friendly services.
          </p> */}
        </section>

        {/* About Narrative */}
        <section className="mt-16 mb-16">
          <div className="grid md:grid-cols-2 gap-12 text-left text-ophira-mediumgray/90 leading-relaxed">
            <div className="space-y-6">
              <p>
                YouAux started with a simple idea: <b>independent artists</b> deserve the same <b>global reach</b> as major labels without losing <b>ownership</b> or paying hidden costs. From our first releases to today, we have focused on removing barriers in distribution, support, and transparency so creators can stay in control.
              </p>
              <p>
                We named the company <b>YouAux</b>, a combination of <b>"You"</b> and <b>"Aux"</b>. <b>"You"</b> represents the artist, and <b>"Aux"</b> is the music cable that carries sound, symbolizing how <b>we connect you and your music </b> to the world.
              </p>
              <p>
                Built by <b>technologists and music professionals</b>, YouAux blends <b>automation with human support</b>. Our in-house tools streamline uploads, metadata accuracy, payouts, and rights management, while our team helps artists navigate platforms, policies, and opportunities.
              </p>
              
            </div>
            <div className="space-y-6">
              <p>
                Today, YouAux empowers creators across genres to reach <b>150+ platforms</b>, retain <b>100% of their rights</b>, and get timely guidance via live chat and dedicated support. Trusted by <b>50K+ artists worldwide</b>, we invest in reliable infrastructure, compliance, and partnerships to ensure your music is available, protected, and ready to grow globally.
              </p>
              <p>
                We continue to expand services, from <b>unlimited releases</b> and <b>CMS support</b> to <b>roster tools for labels</b>, so artists and partners can manage <b>catalogs, analytics, and revenue</b> from one place. Every feature we ship is designed to keep the <b>creator at the center</b>.
              </p>
            </div>
          </div>
        </section>
        
         {/* Why Choose Us Section */}
         <section className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              WHY CHOOSE US?
            </h2>
            <p className="text-ophira-mediumgray/80 max-w-3xl mx-auto">
              YouAux provides independent musicians the tools they need to get their music out to the world, grow their fan base, and stay in control of their careers.
            </p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-6">
            <Card className="w-full sm:w-[280px] md:w-[300px] lg:w-[320px] max-w-[320px] border border-ophira-yellow/40 bg-[#0A0E1A]/20 hover:border-ophira-yellow/70 transition-all duration-300 rounded-xl">
              <CardContent className="p-6 flex flex-col items-center text-center gap-3">
                <Headphones size={44} className="text-ophira-yellow" />
                <h3 className="text-xl font-bold text-white">Global Reach</h3>
                <p className="text-ophira-mediumgray text-sm">Distribute your music to 150+ streaming platforms worldwide with just a few clicks.</p>
              </CardContent>
            </Card>
            

            <Card className="w-full sm:w-[280px] md:w-[300px] lg:w-[320px] max-w-[320px] border border-ophira-yellow/40 bg-[#0A0E1A]/20 hover:border-ophira-yellow/70 transition-all duration-300 rounded-xl">
              <CardContent className="p-6 flex flex-col items-center text-center gap-3">
                <Music size={44} className="text-ophira-yellow" />
                <h3 className="text-xl font-bold text-white">Unlimited Release</h3>
                <p className="text-ophira-mediumgray text-sm">Publish unlimited releases with unlimited artist names without extra fees.</p>
              </CardContent>
            </Card>

            <Card className="w-full sm:w-[280px] md:w-[300px] lg:w-[320px] max-w-[320px] border border-ophira-yellow/40 bg-[#0A0E1A]/20 hover:border-ophira-yellow/70 transition-all duration-300 rounded-xl">
              <CardContent className="p-6 flex flex-col items-center text-center gap-3">
                <Users size={44} className="text-ophira-yellow" />
                <h3 className="text-xl font-bold text-white">Guaranteed Reply</h3>
                <p className="text-ophira-mediumgray text-sm">Response within 24 hours + 24/7 live chat support when you need it.</p>
              </CardContent>
            </Card>
            <Card className="w-full sm:w-[280px] md:w-[300px] lg:w-[320px] max-w-[320px] border border-ophira-yellow/40 bg-[#0A0E1A]/20 hover:border-ophira-yellow/70 transition-all duration-300 rounded-xl">
              <CardContent className="p-6 flex flex-col items-center text-center gap-3">
                <Globe size={44} className="text-ophira-yellow" />
                <h3 className="text-xl font-bold text-white">CMS Service</h3>
                <p className="text-ophira-mediumgray text-sm">Get Video Content ID and Rights Management tools to protect your music.</p>
              </CardContent>
            </Card>

            <Card className="w-full sm:w-[280px] md:w-[300px] lg:w-[320px] max-w-[320px] border border-ophira-yellow/40 bg-[#0A0E1A]/20 hover:border-ophira-yellow/70 transition-all duration-300 rounded-xl">
              <CardContent className="p-6 flex flex-col items-center text-center gap-3">
                <Award size={44} className="text-ophira-yellow" />
                <h3 className="text-xl font-bold text-white">Keep 100% Rights</h3>
                <p className="text-ophira-mediumgray text-sm">Maintain full ownership of your music while we help you monetize it effectively.</p>
              </CardContent>
            </Card>
            

            <Card className="w-full sm:w-[280px] md:w-[300px] lg:w-[320px] max-w-[320px] border border-ophira-yellow/40 bg-[#0A0E1A]/20 hover:border-ophira-yellow/70 transition-all duration-300 rounded-xl">
              <CardContent className="p-6 flex flex-col items-center text-center gap-3">
                <Target size={44} className="text-ophira-yellow" />
                <h3 className="text-xl font-bold text-white">Record Label Platform</h3>
                <p className="text-ophira-mediumgray text-sm">Help you launch your own music with verified music labels on YouTube.</p>
              </CardContent>
            </Card>

            <Card className="w-full sm:w-[280px] md:w-[300px] lg:w-[320px] max-w-[320px] border border-ophira-yellow/40 bg-[#0A0E1A]/20 hover:border-ophira-yellow/70 transition-all duration-300 rounded-xl ">
              <CardContent className="p-6 flex flex-col items-center text-center gap-3">
                <Music size={44} className="text-ophira-yellow" />
                <h3 className="text-xl font-bold text-white">Playlist Pitching</h3>
                <p className="text-ophira-mediumgray text-sm">Dedicated support to pitch your tracks to editorial and algorithmic playlists.</p>
              </CardContent>
            </Card>

            <a
              href={DASHBOARD_SIGNIN_URL}
              className="w-full sm:w-[60px] md:w-[80px] lg:w-[100px] max-w-[100px] bg-transparent transition-all duration-300 flex flex-col items-center justify-center gap-3 py-8 text-ophira-yellow hover:text-white"
            >
              <span className="flex items-center justify-center w-12 h-12 rounded-full border border-ophira-yellow/60 group-hover:border-ophira-yellow bg-transparent group-hover:bg-ophira-yellow/10 transition-all duration-300">
                <ArrowRight size={22} className="group-hover:translate-x-1 transition-transform duration-300" />
              </span>
              <span className="text-sm font-semibold uppercase tracking-wide">More</span>
            </a>
          </div>
        </section>

               
        {/* Leaders Section */}
        <section className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
              Our Pillar&apos;s
            </h2>
            <p className="text-ophira-mediumgray max-w-3xl mx-auto">
              Meet the visionary leaders driving YouAux forward, combining deep expertise in Music Distribution Technology, Artist Relations, and Digital Innovation.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {leaders.map((leader) => (
              <div
                key={leader.name}
                className="group h-full overflow-hidden rounded-xl border border-ophira-yellow/20 bg-gradient-to-b from-ophira-darkgray/40 to-black shadow-lg"
              >
                <div className="relative aspect-[3/4] w-full">
                  <img
                    src={leader.image}
                    alt={leader.name}
                    className="absolute inset-0 h-full w-full object-cover grayscale transition duration-500 group-hover:grayscale-0 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent"></div>
                  <div className="absolute inset-x-0 bottom-[-12px] p-2 ml-2">
                    <div className="flex flex-col gap-0 min-h-[130px]">
                      <h3 className="text-xl font-bold text-white">{leader.name}</h3>
                      <p className="text-ophira-yellow/80 text-sm font-semibold uppercase tracking-wide mb-1">{leader.title}</p>
                      <p className="text-ophira-mediumgray text-[12px] leading-relaxed flex-1">{leader.description}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

       {/* Our Mission */}
       <section className="mb-20 text-center">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              OUR MISSION
            </h2>
            <p className="text-ophira-mediumgray text-lg mb-6">
              Our vision is to build an artist‑first ecosystem where independent creators can grow without limits.
            </p>
            <p className="text-ophira-mediumgray text-lg mb-6">
              We continuously invest in platform development, smarter tools, clearer communication, and personalised support to help artists move faster and plan better. From early releases to global expansion, our focus is on long‑term success, not short‑term wins.
            </p>
            <p className="text-ophira-mediumgray text-lg">
              As the creator economy evolves, we aim to remain a trusted partner, adapting, improving, and innovating so our community can thrive in an ever‑changing music landscape.
            </p>
          </div>
        </section>

        {/* Our Aim Section */}
        <section className="grid md:grid-cols-2 gap-10 mb-20 items-center">
          <div className="order-2 md:order-1">
            <div className="ophira-section-label">WHERE DO WE WANT TO BE?</div>
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              OUR AIM
            </h2>
            <p className="text-ophira-mediumgray mb-6">
              Our aim is to turn artistic momentum into sustainable growth. We help independent musicians move beyond releases and streams by building lasting brands, stronger fan relationships, and smarter careers.
            </p>  
            <p className="text-ophira-mediumgray mb-6">
              From global distribution and sync-ready delivery to playlist pitching insights and performance analytics, our tools are designed to help artists compete with confidence in a crowded market. Every feature is built to reduce friction, save time, and unlock new opportunities without compromising creative control.
            </p>
            <p className="text-ophira-mediumgray mb-6">
              At the core, our goal is simple: give independent artists a clearer path to visibility, income, and long‑term independence.
            </p>
          </div>
          <div className="order-1 md:order-2">
            <img 
              src="/uploads/our-aim.webp" 
              alt="Our Aim" 
              className="rounded-xl shadow-lg w-full max-w-md mx-auto hover:scale-105 transition-transform duration-500"
            />
          </div>
        </section>

        {/* Why We Started */}
        <section className="grid md:grid-cols-2 gap-12 mb-20 items-center">
          <div>
            <img 
              src="/uploads/why-we-started.webp" 
              alt="Why We Started" 
              className="rounded-xl shadow-lg w-full max-w-md mx-auto hover:scale-105 transition-transform duration-500"
            />
          </div>
          <div>
            <div className="ophira-section-label">OUR STORY</div>
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              WHY WE STARTED
            </h2>
            <p className="text-ophira-mediumgray mb-6">
              Independent music in India is evolving fast, with more voices, genres, and audiences than ever before. Yet many talented artists still struggle with limited access, unclear monetisation paths, and a lack of reliable guidance.
            </p>
            <p className="text-ophira-mediumgray mb-6">
              YouAux was created to bridge that gap. We saw a need for a platform that understands local realities while operating at a global standard, one that helps artists grow at home and scale internationally.
            </p>
            <p className="text-ophira-mediumgray">
              By combining technology, industry knowledge, and hands‑on support, we set out to remove guesswork from music distribution and career growth, so artists can focus on what matters most: making music that travels.
            </p>
          </div>
        </section>

        {/* Our Platform Section */}
        <section className="grid md:grid-cols-2 gap-12 mb-20 items-center">
          <div className="order-2 md:order-1">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              OUR PLATFORM
            </h2>
            <p className="text-ophira-mediumgray mb-6">
              Our platform is built to simplify the music business for independent artists - without taking away control. From release to revenue, YouAux combines automation, AI, and human support to help creators manage their music efficiently and scale with confidence.
            </p>
            <p className="text-ophira-mediumgray mb-6">
              With a clean, intuitive dashboard, artists can upload music, manage metadata, track performance, and monitor earnings across platforms, all from one place. Our technology handles the complexity in the background, so you can focus on creating while staying fully in control of your catalog and rights.
            </p>
            <div className="space-y-4 mt-8">
              <div>
                <p className="text-white mb-2">AI Automation</p>
                <div className="w-full bg-ophira-darkgray/30 h-3 rounded-full border border-ophira-yellow/20 overflow-hidden">
                  <div className="bg-ophira-yellow h-3 rounded-full" style={{ width: "90%" }}></div>
                </div>
              </div>
              <div>
                <p className="text-white mb-2">Easy to use CRM</p>
                <div className="w-full bg-ophira-darkgray/30 h-3 rounded-full border border-ophira-yellow/20 overflow-hidden">
                  <div className="bg-ophira-yellow h-3 rounded-full" style={{ width: "85%" }}></div>
                </div>
              </div>
              <div>
                <p className="text-white mb-2">Client Satisfaction</p>
                <div className="w-full bg-ophira-darkgray/30 h-3 rounded-full border border-ophira-yellow/20 overflow-hidden">
                  <div className="bg-ophira-yellow h-3 rounded-full" style={{ width: "95%" }}></div>
                </div>
              </div>
            </div>
          </div>
          <div className="order-1 md:order-2">
            <img 
              src="/uploads/our-platform.webp" 
              alt="YouAux Platform" 
              className="rounded-xl shadow-lg w-full max-w-md mx-auto hover:scale-105 transition-transform duration-500"
            />
          </div>
        </section>

        {/* Trustpilot Reviews */}
        <section className="mb-20">
            <div className="text-center pt-10 mb-12">
              <div className="ophira-section-label text-[18px] text-ophira-yellow flash-line">TRUSTED BY 50K+ ARTISTS WORLDWIDE</div>
            <h2 className="text-4xl md:text-5xl pt-2 font-bold mb-4 text-white">What Artists Say on Trustpilot</h2>
            <p className="text-ophira-mediumgray text-[18px] max-w-[650px] mx-auto">
              Real feedback from creators using YouAux to distribute, protect, and grow their music.
            </p>
          </div>
          <div className="relative overflow-hidden">
            <button
              aria-label="Previous"
              onClick={handlePrev}
              className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-black/40 hover:bg-black/60 text-white rounded-full w-10 h-10 items-center justify-center"
            >
              ‹
            </button>
            <button
              aria-label="Next"
              onClick={handleNext}
              className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-black/40 hover:bg-black/60 text-white rounded-full w-10 h-10 items-center justify-center"
            >
              ›
            </button>
            <div
              className={`flex ${isTransitioning ? "transition-transform duration-700 ease-out" : ""}`}
              style={{ transform: `translateX(-${(100 / 3) * reviewIndex}%)` }}
            >
              {sliderColumns.map((column, idx) => (
                <div
                  key={idx}
                  className="w-full sm:w-1/2 md:w-1/3 flex-shrink-0 px-2 flex"
                >
                  <div className="flex flex-col gap-4 w-full h-full">
                    {column.items.map((review, innerIdx) => {
                      const quoteKey = `${review.name}-${review.date}-${innerIdx}`;
                      const showMore = review.quote.length > 150;
                      const isExpanded = expandedReviews[quoteKey];
                      const palette = ["bg-amber-200", "bg-sky-200", "bg-emerald-200", "bg-indigo-200", "bg-rose-200", "bg-yellow-200", "bg-lime-200"];
                      const colorClass = palette[(review.name.charCodeAt(0) + review.name.length) % palette.length];
                      const displayQuote = review.quote;

                      return (
                      <div
                        key={quoteKey}
                        className={`w-full rounded-xl border border-ophira-yellow/25 bg-white text-slate-900 p-6 shadow-[0_10px_30px_rgba(0,0,0,0.12)] transition-all duration-300 flex flex-col gap-3 ${
                          isExpanded ? "h-auto" : "h-[300px]"
                        }`}
                      >
                        <div className="flex items-center gap-3 mb-3">
                          {review.avatar ? (
                            <img
                              src={review.avatar}
                              alt={`${review.name} avatar`}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className={`w-10 h-10 rounded-full ${colorClass} text-slate-900 flex items-center justify-center font-semibold`}>
                              {review.name
                                .split(" ")
                                .map((n) => n[0])
                                .join("")
                                .slice(0, 2)
                                .toUpperCase()}
                            </div>
                          )}
                          <div>
                            <p className="text-slate-900 font-semibold leading-tight">{review.name}</p>
                            <p className="text-slate-500 text-xs">{review.country} · {review.reviewCount}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-0.5 mb-3">
                          {Array.from({ length: 5 }).map((_, starIdx) => (
                            <Star
                              key={starIdx}
                              size={16}
                              className={`w-5 h-5 p-[3px] ${starIdx < review.rating
                                ? "bg-[#8ED841] text-white fill-white stroke-white"
                                : "bg-slate-200 text-white fill-white stroke-white"}`}
                              strokeWidth={1.5}
                            />
                          ))}
                          {review.verified && (
                            <span className="flex items-center gap-1 text-xs font-semibold text-[#00b67a]">
                              <Check size={14} /> Verified
                            </span>
                          )}
                        </div>
                        <div className={`flex-1 flex flex-col ${isExpanded ? "" : "overflow-hidden"}`}>
                          <p className={`text-slate-800 leading-relaxed text-[15px] ${showMore && !isExpanded ? "line-clamp-3" : ""}`}>“{displayQuote}”</p>
                          {showMore && (
                            <button
                              className="mt-2 text-black text-sm font-semibold hover:text-black/80 transition-colors self-start"
                              onClick={() =>
                                setExpandedReviews(() => {
                                  const next: Record<string, boolean> = {};
                                  if (!isExpanded) next[quoteKey] = true;
                                  return next;
                                })
                              }
                            >
                              {isExpanded ? "Show less" : "Read more"}
                            </button>
                          )}
                        </div>
                        <div className="mt-auto pt-0">
                          <span className="inline-flex px-2.5 py-1 rounded-[6px] bg-slate-200 text-slate-900 text-xs font-medium">
                            {review.date}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
            </div>
          </div>
          <div className="text-center mt-8 text-slate-200">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-ophira-yellow/60 text-ophira-yellow bg-transparent">
              4.5+ Rating on Trustpilot
            </span>
          </div>
        </section>

        

      


        {/* Vision Section */}
        <section className="text-center mb-2">
          <div className="max-w-4xl mx-auto">
            {/* <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              OUR VISION
            </h2>
            <p className="text-ophira-mediumgray text-lg mb-8">
              We don't stop building out our platform, features, lines of communication, tool development, end user experience, one on one support, all to help our family succeed faster.
            </p> */}
            <Link to="/services">
              <Button className="ophira-button px-8 py-6 hover:bg-ophira-yellow rounded-full">
                Explore Our Services
              </Button>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
    </>
  );
};

const AboutHeader = () => {
  const [isVisible, setIsVisible] = useState(false);
  const headerRef = useRef(null);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <h1 
      ref={headerRef}
      className={`text-4xl md:text-5xl lg:text-6xl font-bold mb-6 transition-all duration-1000 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}
    >
      About <span className="text-ophira-yellow">YouAux</span>
      <span className="block text-6xl md:text-8xl font-bold text-outline text-transparent absolute -z-10 left-1/2 -translate-x-1/2 opacity-10">ABOUT</span>
    </h1>
  );
};

const MilestoneCard = ({ icon, number, label, delay }) => {
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
    };
  }, []);

  return (
    <div 
      ref={cardRef}
      className={`ophira-card p-8 text-center hover:scale-105 transition-all duration-700 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'
      }`}
      style={{ transitionDelay: `${delay * 200}ms` }}
    >
      <div className="relative w-24 h-24 mx-auto mb-6 bg-ophira-yellow/10 rounded-full flex items-center justify-center">
        <div className="absolute inset-0 bg-ophira-yellow/5 rounded-full animate-pulse-glow"></div>
        {icon}
      </div>
      <h3 className="text-4xl font-bold mb-2 text-white">
        {number}
      </h3>
      <p className="text-ophira-mediumgray">{label}</p>
    </div>
  );
};

export default About;
